import { useMemo, useState } from "react";

import { PageHeader } from "../../components/ui/PageHeader";
import { Timestamp } from "../../components/ui/Timestamp";
import { useAudit } from "../../hooks/useAudit";
import type { AuditEntryRow, AuditOutcome } from "../../api/audit";
import { ScrollableTable } from "../../components/ScrollableTable";

type OutcomeFilter = "ALL" | AuditOutcome;

const OUTCOME_OPTIONS: OutcomeFilter[] = [
  "ALL",
  "pass",
  "violation",
  "safe_utterance_substitution",
  "opening_greeting",
  "recording_disclosure",
  "recording_started",
  "recording_stopped",
  "recording_failed",
  "recording_accessed",
  "sip_outbound_initiated",
  "sip_outbound_failed",
];

// Shared column track for the audit ledger header + every row (was duplicated
// as an inline literal in both places). Fixed tracks sum to 560px; the 1fr
// Outcome column + gaps/padding put the comfortable min around 760px.
const AUDIT_GRID = "60px 100px 1fr 130px 140px 130px";
const AUDIT_TABLE_MIN_WIDTH = 760;

function outcomeColor(outcome: AuditOutcome): string {
  switch (outcome) {
    case "pass":
    case "recording_disclosure":
    case "recording_started":
    case "recording_stopped":
    case "evidence_tool_invoked":
    case "voice_cloned":
      return "var(--success)";
    case "violation":
    case "recording_failed":
    case "sip_outbound_failed":
    case "post_hoc_violation_hangup":
    case "evidence_tool_blocked_out_of_scope":
      return "var(--error)";
    case "safe_utterance_substitution":
    case "opening_greeting":
    case "sip_outbound_initiated":
    case "max_turns_reached_hangup":
    case "agent_initiated_hangup":
      return "var(--accent-bright)";
    case "recording_accessed":
      return "var(--warning)";
  }
}

function shortHash(h: string): string {
  return h.length > 12 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h;
}

function shortUuid(u: string): string {
  return u.split("-")[0];
}

export function AuditList() {
  const { data, isLoading, isError } = useAudit(200);
  const [filter, setFilter] = useState<OutcomeFilter>("ALL");

  const filtered = useMemo<AuditEntryRow[]>(() => {
    const items = data?.items ?? [];
    if (filter === "ALL") return items;
    return items.filter((it) => it.outcome === filter);
  }, [data, filter]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
        Loading audit chain…
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 24, color: "var(--error)", fontFamily: "var(--font-sans)" }}>
        Failed to load audit chain.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 1200 }}>
      <PageHeader
        eyebrow="Operator console"
        title="Cryptographic audit log"
        subtitle="Hash-chained ledger of every action the system took during authorized operations. Each entry references the prior hash; the head is the latest tip."
        meta={
          <span>
            {filter === "ALL"
              ? `${data?.total ?? 0} ENTRIES · NEWEST 200 SHOWN`
              : `${filtered.length} OF ${data?.total ?? 0} · FILTER ${filter.toUpperCase()}`}
          </span>
        }
        actions={
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-input)",
              padding: "8px 12px",
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              letterSpacing: "0.06em",
            }}
            title="Hash of the most recent entry — verifies chain tip."
          >
            <span
              style={{
                color: "var(--text-muted)",
                fontSize: 9.5,
                letterSpacing: "0.18em",
              }}
            >
              HEAD
            </span>
            <span style={{ color: "var(--accent)" }}>
              {data?.head_hash ? shortHash(data.head_hash) : "—"}
            </span>
          </div>
        }
      />

      <OutcomeFilterBar value={filter} onChange={setFilter} />

      {filtered.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            padding: "48px 24px",
            textAlign: "center",
            color: "var(--text-muted)",
            fontSize: 13,
          }}
        >
          {filter === "ALL"
            ? "Audit chain is empty."
            : `No ${filter} entries.`}
        </div>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            overflow: "hidden",
            marginTop: 12,
          }}
        >
          <ScrollableTable minWidth={AUDIT_TABLE_MIN_WIDTH}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: AUDIT_GRID,
              gap: 12,
              padding: "10px 16px",
              borderBottom: "1px solid var(--border-subtle)",
              background: "var(--bg-secondary)",
            }}
          >
            {["#", "Ago", "Outcome", "Engagement", "Action", "Hash"].map((h) => (
              <span
                key={h}
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--text-muted)",
                }}
              >
                {h}
              </span>
            ))}
          </div>

          {filtered.map((row) => (
            <div
              key={row.entry_hash}
              style={{
                display: "grid",
                gridTemplateColumns: AUDIT_GRID,
                gap: 12,
                padding: "10px 16px",
                borderBottom: "1px solid var(--border-subtle)",
                alignItems: "center",
                fontSize: 11,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--text-muted)",
                }}
              >
                {row.index}
              </span>
              <Timestamp iso={row.timestamp} variant="stacked" align="left" />
              <span>
                <span
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    color: outcomeColor(row.outcome),
                    border: `1px solid ${outcomeColor(row.outcome)}40`,
                    background: `${outcomeColor(row.outcome)}14`,
                    borderRadius: 4,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {row.outcome.replace(/_/g, " ")}
                </span>
                {row.violation_code ? (
                  <span
                    style={{
                      marginLeft: 8,
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--error)",
                    }}
                    title={row.violation_reason ?? undefined}
                  >
                    {row.violation_code}
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--text-secondary)",
                  fontSize: 10.5,
                }}
                title={row.engagement_id}
              >
                {shortUuid(row.engagement_id)}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--text-secondary)",
                  fontSize: 10.5,
                }}
                title={row.action_id}
              >
                {shortUuid(row.action_id)}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--text-muted)",
                  fontSize: 10,
                }}
                title={`entry: ${row.entry_hash}\nprev: ${row.prev_hash}`}
              >
                {shortHash(row.entry_hash)}
              </span>
            </div>
          ))}
          </ScrollableTable>
        </div>
      )}
    </div>
  );
}

function OutcomeFilterBar({
  value,
  onChange,
}: {
  value: OutcomeFilter;
  onChange: (v: OutcomeFilter) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 4,
      }}
    >
      {OUTCOME_OPTIONS.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: "4px 10px",
              border: "1px solid var(--border)",
              background: active ? "var(--accent-subtle)" : "var(--bg-tertiary)",
              color: active ? "var(--accent-bright)" : "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              fontWeight: active ? 700 : 500,
              borderRadius: 4,
            }}
          >
            {opt.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}
